import React, { FC, SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { flatten } from 'lodash-es';
import { CampaignStatus, CampaignTypeFilter } from '@dev/ee-api/ee-api-models';
import {
    CustomTableHeadersProps,
    FilterIcon,
    palette,
    SimplePopover as Popover,
    Tag,
    TertiaryButton,
    TertiaryButtonGroup,
} from '@dev/ee-components';

import useMobile from '@/services/hooks/useMobile';
import { ApplicationState } from '@/store';
import { DeleteCampaigns, GetCampaigns, UnpickAllCampaigns } from '@/store/campaigns/actions';

import { useRequestParams } from './useRequestParams';
import { useCampaignStatuses } from './useCampaignStatuses';
import { FilterContainer, FilterLabel } from '../../assets/EventFilter/styledComponents';

const getCustomPopoverBody = (element: { label: string; count: number }): JSX.Element => (
    <FilterContainer>
        <FilterLabel>{element?.label}</FilterLabel>
        <Tag label={element?.count} clickable={false} />
    </FilterContainer>
);

const StatusFilters: FC = (): JSX.Element => {
    const dispatch = useDispatch();
    const requestParams = useRequestParams();
    const defaultValue = requestParams?.status ? flatten([requestParams?.status]) : [''];

    const options = useCampaignStatuses();

    const onFilterClick = (event: SyntheticEvent<HTMLInputElement>, checkedStatus: CampaignStatus[]): void => {
        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns({ ...requestParams, status: checkedStatus }));
    };

    if (options.length > 1) {
        return (
            <Popover
                type="icon"
                options={options}
                enableCheckbox
                defaultCheckboxValue={defaultValue}
                customDropdownBody={(element: { label: string; count: number }): JSX.Element =>
                    getCustomPopoverBody(element)
                }
                onCheckboxClick={onFilterClick}
                popperProps={{
                    disablePortal: false,
                }}
            >
                <FilterIcon color="#708097" />
            </Popover>
        );
    }
    return null;
};

export const useTableHeaders = (
    openExportAutorespondersModal: () => void,
    screenCampaignType: CampaignTypeFilter[]
): (CustomTableHeadersProps | string)[] => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const { campaigns } = useSelector((state: ApplicationState) => state);

    if (isMobile) {
        return [t('triggeraudience'), t('status'), t('delivered'), t('opened'), t('clicked')];
    }

    if (campaigns.checkedCampaigns.length > 0) {
        return [
            {
                id: 'preview',
                numeric: false,
                label: t('campaigns'),
            },
            {
                id: 'campaignName',
                numeric: false,
                label: '',
            },
            {
                id: 'delivered',
                numeric: false,
                label: '',
                headerAlign: 'right',
            },
            {
                id: 'opened',
                numeric: false,
                label: '',
                headerAlign: 'right',
            },
            {
                id: 'clicked',
                numeric: false,
                label: '',
                headerAlign: 'right',
            },
            { id: 'spaceColumn', numeric: false, label: '' },
            {
                id: 'status',
                numeric: false,
                label: '',
            },
            {
                id: 'viewReport',
                numeric: false,
                label: '',
                headerAlign: 'right',
                columnAlign: 'center',
                filterIcon: (
                    <TertiaryButtonGroup>
                        <TertiaryButton
                            style={{ margin: 0, color: palette.indigo[6] }}
                            text={t('export')}
                            onClick={openExportAutorespondersModal}
                        />
                        <TertiaryButton
                            style={{ margin: 0, color: palette.red[6] }}
                            text={t('delete')}
                            onClick={() => {
                                dispatch(DeleteCampaigns(campaigns.checkedCampaigns, screenCampaignType));
                            }}
                        />
                    </TertiaryButtonGroup>
                ),
            },
        ];
    }

    return [
        {
            id: 'preview',
            numeric: false,
            label: '',
        },
        {
            id: 'campaignName',
            label: t('name'),
            numeric: false,
        },
        {
            id: 'delivered',
            label: t('delivered'),
            numeric: false,
            headerAlign: 'right',
        },
        {
            id: 'opened',
            label: t('opened'),
            numeric: false,
            headerAlign: 'right',
        },
        {
            id: 'clicked',
            label: t('clicked'),
            numeric: false,
            headerAlign: 'right',
        },
        { id: 'spaceColumn', numeric: false, label: '' },
        {
            id: 'status',
            label: t('status'),
            numeric: false,
            filterIcon: <StatusFilters />,
        },
        {
            id: 'viewReport',
            numeric: false,
            columnAlign: 'center',
        },
    ];
};
